import { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useTheme, useMediaQuery } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AppSnackbar from './AppSnackbar';
import ManageHelpDialog from './popups/Managehelpdialog';
import { API_BASE_URL } from '../constants/constants';
import { getAuthToken, getLoggedInUser } from '../utils/auth';
import { COLORS, FONT_FAMILY, FONT_HEADING, BUTTON_HOVER_SX } from '../talkTreeShared/Tree/constants/theme';
import { getSimulatedTrees, deleteSimulatedTree, subscribeToSimulatedTrees } from '../talkTreeShared/Tree/utils/simulatedTreeStorage';

const PIPELINE_STEP_MAP = {
  data_retriever: { step: 'Step 1 of 6', label: 'Transcribing' },
  media_transcriber: { step: 'Step 1 of 6', label: 'Transcribing' },
  speaker_diarizer: { step: 'Step 2 of 6', label: 'Diarizing' },
  anonymizer: { step: 'Step 2 of 6', label: 'Anonymizing' },
  storage_manager_initial: { step: 'Step 3 of 6', label: 'Classifying' },
  translation: { step: 'Step 3 of 6', label: 'Classifying' },
  word_cloud: { step: 'Step 3 of 6', label: 'Classifying' },
  math_terms: { step: 'Step 3 of 6', label: 'Classifying' },
  student_talkmoves: { step: 'Step 3 of 6', label: 'Classifying' },
  tutor_talkmoves: { step: 'Step 3 of 6', label: 'Classifying' },
  bci_classifier: { step: 'Step 4 of 6', label: 'Classifying' },
  tutor_talk_move_noticings: { step: 'Step 4 of 6', label: 'Reviewing' },
  student_talk_move_noticings: { step: 'Step 4 of 6', label: 'Reviewing' },
  storage_manager_service_annotations_1: { step: 'Step 4 of 6', label: 'Reviewing' },
  storage_manager_service_annotations_2: { step: 'Step 4 of 6', label: 'Reviewing' },
  storage_manager_service_annotations_3: { step: 'Step 4 of 6', label: 'Reviewing' },
  storage_manager_annotated_transcript: { step: 'Step 4 of 6', label: 'Reviewing' },
  discourse_stats: { step: 'Step 5 of 6', label: 'Summarizing' },
  session_summary: { step: 'Step 5 of 6', label: 'Summarizing' },
  talk_feedback: { step: 'Step 5 of 6', label: 'Summarizing' },
  storage_manager_stats: { step: 'Step 6 of 6', label: 'Generating Tree' },
};

const LABEL_DIALOG_CONTENT = {
  'Transcribing': 'Transcribing involves either converting the provided audio recording to text using automated speech recognition or converting the supplied transcript into the format used internally by Talk Trees.',
  'Diarizing': 'Diarizing means attempting to determine which transcript elements represent student speech and which represent teacher speech.',
  'Anonymizing': 'Anonymizing means removing all text that looks like names or personal identifiers from the transcript.',
  'Classifying': 'During Classifying the Talk Trees application uses multiple techniques to find academically productive and belonging-centered talk in the transcript.',
  'Reviewing': 'Reviewing includes double-checking transcript classifications and finding good examples (for future use).',
  'Summarizing': 'Summarizing includes counting each of the classifications and generating text-based summaries (for future use).',
  'Generating Tree': 'The last step is Generating the Tree in which the data is made available and ready for use by you in the Talk Trees application.',
  'No Talk Transcribed': 'This recording did not contain any transcribable student talk, so a Talk Tree could not be generated for it.',
};

const POLL_INTERVAL_MS = 30 * 1000;

const SIMULATED_ROW_BG = COLORS.tintedHover;
const SIMULATED_BADGE_COLOR = COLORS.success;
const SIMULATED_DESCRIPTION = 'Simulated student talk tree based on your classroom observation.';

const normalizeUploadTime = (session) => {
  const t = parseInt(session.uploadTime) || 0;
  return { ...session, uploadTime: String(t).length === 10 ? t * 1000 : t };
};

const thBase = {
  padding: '0.625rem 1rem',
  fontSize: '0.75rem',
  color: '#111827',
  fontWeight: 500,
  textAlign: 'left',
  borderBottom: `1px solid ${COLORS.hairline}`,
  whiteSpace: 'nowrap',
  backgroundColor: '#F9F7F6',
};

// ── Defined outside SessionStatus to avoid stale closure on every render ──
const SortableHeader = ({ label, sortKey, sortConfig, onSort, style }) => {
  const isActive = sortConfig.key === sortKey;
  const isDesc = sortConfig.direction === 'desc';

  return (
    <th
      style={{ ...thBase, ...style, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.1875rem' }}>
        <span>{label}</span>
        {isActive ? (
          isDesc
            ? <ArrowDownwardIcon sx={{ fontSize: '0.875rem', color: '#111827' }} />
            : <ArrowUpwardIcon sx={{ fontSize: '0.875rem', color: '#111827' }} />
        ) : (
          <UnfoldMoreIcon sx={{ fontSize: '0.875rem', color: '#9ca3af' }} />
        )}
      </Box>
    </th>
  );
};

const SessionStatus = ({ isTokenExpired, setActiveMenu, onSessionSelect, toast, onToastClose }) => {
  const userId = getLoggedInUser() || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sessions, setSessions] = useState([]);
  const [simulatedTrees, setSimulatedTrees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sessionToDelete, setSessionToDelete] = useState(null); 
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteToast, setDeleteToast] = useState(null);
  const [dialogLabel, setDialogLabel] = useState(null);
  const [dataInfoOpen, setDataInfoOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'uploadTime', direction: 'desc' });
  const deleteToastTimerRef = useRef(null);

  const dataFetched = useRef(false);
  const lastFetchTime = useRef(null);
  const prevStatusMap = useRef({});
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(false);
  const sessionsRef = useRef([]);


  // A completed session with an empty/untranscribable transcript is never
  // "ready" (there is no tree to view), and is treated as finished rather
  // than in-progress so polling stops for it.
  const isTreeReady = (status, link, emptyTranscript) =>
    status?.toLowerCase() === 'completed' && link != null && !emptyTranscript;

  const isInProgress = (status, link, emptyTranscript) =>
    !isTreeReady(status, link, emptyTranscript) &&
    !(status?.toLowerCase() === 'completed' && emptyTranscript === true) &&
    !status?.toLowerCase().startsWith('error');


  const fetchSessions = async () => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}v1aux/mediaAnalysisStatus?userId=${encodeURIComponent(userId)}`,
      { method: 'GET', headers: { Authorization: authToken } }
    );
    if (!response.ok) throw new Error(`Failed to fetch recordings: ${response.status}`);
    const data = await response.json();
    // console.log('Raw session data:', JSON.stringify(data, null, 2));
    return data;
  };

  const loadSessions = async (forceRefresh = false) => {
    console.log('[loadSessions] called, forceRefresh=', forceRefresh, 'dataFetched=', dataFetched.current);

    if (!userId) { setError('No logged-in user found. Please log in again.'); return; }

    if (dataFetched.current && !forceRefresh) return;

    const CACHE_DURATION = 5 * 60 * 1000;
    if (lastFetchTime.current && !forceRefresh) {
      if (Date.now() - lastFetchTime.current < CACHE_DURATION) return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await fetchSessions();
      const sessionList = (Array.isArray(data) ? data : []).map(normalizeUploadTime);
      setSessions(sessionList);
      sessionsRef.current = sessionList;

      const newStatusMap = {};
      sessionList.forEach(session => {
        const prev = prevStatusMap.current[session.sessionId];
        const curr = session.status;
        newStatusMap[session.sessionId] = curr;


      });
      prevStatusMap.current = newStatusMap;

      dataFetched.current = true;
      lastFetchTime.current = Date.now();
      setLastUpdated(new Date());
      mixpanel.track('processingStatusPageViewed', { sessionCount: sessionList.length });
      schedulePillPoll(sessionList);
    } catch (err) {
      setError(err.message);
      setSessions([]);
      sessionsRef.current = [];
      dataFetched.current = false;
      mixpanel.track('processingStatusLoadFailed', { error: err.message || 'Unknown error' });
    } finally {
      setIsLoading(false);
    }
  };

  const pollPillStatuses = async () => {
    const current = sessionsRef.current;
    const inProgress = current.filter(s => isInProgress(s.status, s.link, s.emptyTranscript));
    if (inProgress.length === 0) return;

    try {
      if (isTokenExpired) await isTokenExpired();
      const authToken = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}v1aux/mediaAnalysisStatus?userId=${encodeURIComponent(userId)}`,
        { method: 'GET', headers: { Authorization: authToken } }
      );
      if (!response.ok) { schedulePillPoll(current); return; }

      const data = await response.json();

      setSessions(prev => {
        const updated = prev.map(existingSession => {
          if (!isInProgress(existingSession.status, existingSession.link, existingSession.emptyTranscript)) return existingSession;
          const fresh = data.find(d => d.sessionId === existingSession.sessionId);
          if (!fresh) return existingSession;

          if (existingSession.status !== fresh.status || existingSession.link !== fresh.link) {
            console.log(`[PillPoll Status Change] sessionId=${existingSession.sessionId} | "${existingSession.status}" → "${fresh.status}"`);
            prevStatusMap.current[existingSession.sessionId] = fresh.status;
          }

          return { ...existingSession, status: fresh.status, link: fresh.link, emptyTranscript: fresh.emptyTranscript };
        });

        sessionsRef.current = updated;
        return updated;
      });

      setLastUpdated(new Date());

      // Schedule next pill poll if still any in progress
      const stillInProgress = sessionsRef.current.filter(s => isInProgress(s.status, s.link, s.emptyTranscript));
      schedulePillPoll(stillInProgress.length > 0 ? stillInProgress : []);

    } catch (err) {
      console.error('[PillPoll] Error:', err);
      schedulePillPoll(current);
    }
  };

  const schedulePillPoll = (sessionList) => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    const hasInProgress = sessionList.some(s => isInProgress(s.status, s.link, s.emptyTranscript));
    if (hasInProgress) {
      console.log(`[PillPoll] Scheduling next pill poll in ${POLL_INTERVAL_MS / 1000}s`);
      pollTimerRef.current = setTimeout(pollPillStatuses, POLL_INTERVAL_MS);
    } else {
      console.log('[PillPoll] No in-progress sessions, polling stopped.');
    }
  };

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    loadSessions();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => { if (deleteToastTimerRef.current) clearTimeout(deleteToastTimerRef.current); };
  }, []);

  useEffect(() => {
    const loadSimulated = () => setSimulatedTrees(getSimulatedTrees());
    loadSimulated();
    return subscribeToSimulatedTrees(loadSimulated);
  }, []);

  const handleRefresh = () => {
    dataFetched.current = false;
    loadSessions(true);
    mixpanel.track("recordingsRefreshed", {});
  };

  const handleViewClick = (e, sessionId) => {
    e.preventDefault();
    mixpanel.track("talkTreeReadyClicked", { sessionId });
    if (onSessionSelect) onSessionSelect(sessionId);
  };

  const handleSimulatedTreeClick = (treeId) => {
    mixpanel.track("simulatedTreeClicked", { sessionId: treeId });
    if (onSessionSelect) onSessionSelect(treeId);
  };

  // For sessions that are still processing, show the user information
  // about what is happening. To start, we'll just use an alert.
  const handleStatusClick = (status, sessionId) => {
    console.log('Status clicked:', status);
    mixpanel.track("recordingStatusClicked", { status, sessionId });

    if (status?.toLowerCase().startsWith('error')) {
      alert('An error occurred while analyzing your recording.\n\n(Details: ' + status + ')');
      return;
    }

    // Resolve the label for this status and open the dialog
    const { label } = translateProcessingStatus(status, null);
    setDialogLabel(label);
  };

  const handleDeleteClick = (session) => {
    setSessionToDelete({
      sessionId: session.sessionId,
      title: session.title || 'Untitled Session',
      isSimulated: !!session.isSimulated,
    });
    setDeleteDialogOpen(true);
    mixpanel.track(session.isSimulated ? "deleteSimulatedTreeInitiated" : "deleteRecordingInitiated", { sessionId: session.sessionId });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    try {
      if (sessionToDelete.isSimulated) {
        deleteSimulatedTree(sessionToDelete.sessionId);
        mixpanel.track("simulatedTreeDeleted", { sessionId: sessionToDelete.sessionId });

        setDeleteDialogOpen(false);
        setSessionToDelete(null);
        showDeleteToast({
          type: 'success',
          title: 'Simulated tree deleted',
          message: `"${sessionToDelete.title}" has been removed.`,
        });
        return;
      }

      if (isTokenExpired) await isTokenExpired();
      const authToken = getAuthToken();

      const response = await fetch(`${API_BASE_URL}v1aux/deleteTree`, {
        method: 'DELETE',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, sessionIds: [sessionToDelete.sessionId] }),
      });

      if (!response.ok) throw new Error(`Delete failed: ${response.status}`);

      mixpanel.track("recordingDeleted", { sessionId: sessionToDelete.sessionId });

      // Optimistically remove the row from state
      setSessions(prev => {
        const updated = prev.filter(s => s.sessionId !== sessionToDelete.sessionId);
        sessionsRef.current = updated;
        return updated;
      });

      setDeleteDialogOpen(false);
      setSessionToDelete(null);
      showDeleteToast({
        type: 'success',
        title: 'Recording deleted',
        message: `"${sessionToDelete.title}" has been successfully deleted.`,
      });
    } catch (err) {
      console.error('[Delete] Error:', err);
      showDeleteToast({
        type: 'error',
        title: 'Delete failed',
        message: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const showDeleteToast = (toast) => {
    if (deleteToastTimerRef.current) clearTimeout(deleteToastTimerRef.current);
    setDeleteToast(toast);
    deleteToastTimerRef.current = setTimeout(() => setDeleteToast(null), 4000);
  };

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  // Returns a numeric rank for Processing Status so sorting is meaningful:
  // Ready (or completed-but-empty) = highest (6), then by pipeline step
  // number descending, Error = lowest (0)
  const getStatusRank = (status, link, emptyTranscript) => {
    if (isTreeReady(status, link, emptyTranscript)) return 6;
    if (status?.toLowerCase() === 'completed' && emptyTranscript === true) return 6;
    if (status?.toLowerCase().startsWith('error')) return 0;

    const s = status?.toLowerCase() ?? '';
    if (s.startsWith('analyzing')) {
      const pipelineStep = s.replace(/^analyzing\s+/, '').trim();
      const entry = PIPELINE_STEP_MAP[pipelineStep];
      if (entry) {
        const match = entry.step.match(/Step (\d+) of/);
        return match ? parseInt(match[1]) : 1;
      }
    }
    return 1; // default to step 1 (Transcribing)
  };

  const simulatedRows = simulatedTrees.map((tree) => ({
    sessionId: tree.id,
    title: tree.title,
    description: SIMULATED_DESCRIPTION,
    uploadTime: tree.createdAt,
    status: 'simulated',
    link: null,
    isSimulated: true,
  }));

  const allRows = [...sessions.map((s) => ({ ...s, isSimulated: false })), ...simulatedRows];

  const sortedSessions = [...allRows].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal, bVal;

    if (key === 'uploadTime') {
      aVal = parseInt(a.uploadTime) || 0;
      bVal = parseInt(b.uploadTime) || 0;
    } else if (key === 'status') {
      aVal = getStatusRank(a.status, a.link, a.emptyTranscript);
      bVal = getStatusRank(b.status, b.link, b.emptyTranscript);
    } else if (key === 'title') {
      aVal = (a.title || 'Untitled Session').toLowerCase();
      bVal = (b.title || 'Untitled Session').toLowerCase();
    } else if (key === 'description') {
      aVal = (a.description || '').toLowerCase();
      bVal = (b.description || '').toLowerCase();
    } else {
      return 0;
    }

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const translateProcessingStatus = (status, link, emptyTranscript) => {
    if (status?.toLowerCase() === 'completed' && emptyTranscript === true) {
      return { step: 'Step 6 of 6', label: 'No Talk Transcribed' };
    }
    if (status?.toLowerCase() === 'completed' && link == null) {
      return { step: 'Step 6 of 6', label: 'Generating Tree' };
    }

    const s = status?.toLowerCase() ?? '';

    if (s.startsWith('analyzing')) {
      const pipelineStep = s.replace(/^analyzing\s+/, '').trim();
      if (pipelineStep) {
        const entry = PIPELINE_STEP_MAP[pipelineStep];
        if (entry) return entry;

        console.warn(`Unknown pipeline step encountered: "${pipelineStep}"`);
        mixpanel.track('systemWarning', { unexpectedProcessingStep: pipelineStep });
        return { step: 'Step ? of 6', label: 'Analyzing' };
      }
    }

    return { step: 'Step 1 of 6', label: 'Transcribing' };
  };

  const getStepFraction = (step) => {
    if (step === 'Step 6 of 6') return 1;
    const match = step.match(/Step (\d+|\?) of (\d+)/);
    if (!match || match[1] === '?') return 0;
    return parseInt(match[1]) / parseInt(match[2]);
  };

  const td = {
    padding: '0.875rem 1rem',
    fontSize: '0.8125rem',
    color: COLORS.text,
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'top',
    backgroundColor: '#F9F7F6',
  };

  // Belt-and-suspenders fallback: normalizeUploadTime converts to ms at fetch time,
  // but this handles any stray 10-digit (seconds) or 13-digit (ms) uploadTime values.
  const toUploadDate = (uploadTime) => {
    const t = parseInt(uploadTime) || 0;
    return new Date(t < 1e10 ? t * 1000 : t);
  };

  const formatLastUpdated = (date) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      month: 'numeric', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    });
  };

  // Shared between the desktop table cell and the mobile card — one source of
  // truth for what the "Processing Status" indicator looks like for a session.
  const renderStatusIndicator = (session) => {
    if (session.isSimulated) {
      return (
        <Box
          onClick={() => handleSimulatedTreeClick(session.sessionId)}
          role="button"
          tabIndex={0}
          aria-label="Simulated tree — click to view in the Talk Tree Library"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSimulatedTreeClick(session.sessionId); } }}
          sx={{
            position: 'relative',
            width: '9rem',
            height: '1.8125rem',
            borderRadius: '1rem',
            backgroundColor: SIMULATED_BADGE_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>
            Simulated Tree
          </Typography>
        </Box>
      );
    }

    if (session.status?.toLowerCase() === 'completed' && session.emptyTranscript === true) {
      return (
        <Box
          onClick={() => setDialogLabel('No Talk Transcribed')}
          role="button"
          tabIndex={0}
          aria-label="No Talk Transcribed — click for details"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDialogLabel('No Talk Transcribed'); } }}
          sx={{
            position: 'relative',
            width: '9rem',
            height: '1.8125rem',
            borderRadius: '1rem',
            backgroundColor: COLORS.textMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>
            No Talk Transcribed
          </Typography>
        </Box>
      );
    }

    if (isTreeReady(session.status, session.link, session.emptyTranscript)) {
      return (
        <Box
          onClick={(e) => handleViewClick(e, session.sessionId)}
          role="button"
          tabIndex={0}
          aria-label="Talk Tree ready — click to view"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick(e, session.sessionId); } }}
          sx={{
            position: 'relative',
            width: '9rem',
            height: '1.8125rem',
            borderRadius: '1rem',
            backgroundColor: COLORS.success,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>
            Talk Tree Ready
          </Typography>
        </Box>
      );
    }

    if (session.status?.toLowerCase().startsWith('error')) {
      return (
        <Box
          onClick={() => handleStatusClick(session.status, session.sessionId)}
          role="button"
          tabIndex={0}
          aria-label="Error — click for details"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStatusClick(session.status, session.sessionId); } }}
          sx={{
            position: 'relative',
            width: '9rem',
            height: '1.8125rem',
            borderRadius: '1rem',
            backgroundColor: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>
            Error
          </Typography>
        </Box>
      );
    }

    return renderProcessingPill(session);
  };

  const renderProcessingPill = (session) => {
    const { step, label } = translateProcessingStatus(session.status, session.link, session.emptyTranscript);
    const fraction = getStepFraction(step);
    const fillPercent = Math.round(fraction * 100);

    return (
      <Box
        onClick={() => handleStatusClick(session.status, session.sessionId)}
        role="button"
        tabIndex={0}
        aria-label={`Processing status: ${label}, ${step}. Click for more information.`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStatusClick(session.status, session.sessionId); } }}
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          gap: '0.25rem',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '9rem',
            height: '1.8125rem',
            borderRadius: '0.875rem',
            backgroundColor: COLORS.primaryLight,
            overflow: 'hidden',
            border: `1px solid ${COLORS.primaryLight}`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0, top: 0, bottom: 0,
              width: `${fillPercent}%`,
              backgroundColor: COLORS.primary,
              borderRadius: '0.875rem',
              transition: 'width 0.4s ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#fff',
              zIndex: 1,
              textShadow: '0 0 0.25rem rgba(0,0,0,0.3)',
            }}
          >
            {label}
          </Box>
        </Box>

        {/* Step number below */}
        <Typography sx={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          color: COLORS.textMuted,
          lineHeight: 1,
        }}>
          {step}
        </Typography>
      </Box>
    );
  };

  // Shared delete icon-button between table row and mobile card
  const renderDeleteButton = (session, canDelete) => (
    <Box
      component="button"
      onClick={() => canDelete && handleDeleteClick(session)}
      title={canDelete ? 'Delete Talk Tree' : 'Only completed Talk Trees can be deleted'}
      aria-label={canDelete ? `Delete recording: ${session.title || 'Untitled Session'}` : `Cannot delete: only completed Talk Trees can be deleted`}
      aria-disabled={!canDelete}
      sx={{
        background: 'none', border: 'none', padding: '0.25rem', borderRadius: '0.375rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: canDelete ? 'pointer' : 'not-allowed',
        color: canDelete ? COLORS.textMuted : '#d1d5db',
        transition: 'color 0.15s ease, background-color 0.15s ease',
        flexShrink: 0,
        '&:hover': canDelete ? { color: '#dc2626', backgroundColor: '#fef2f2' } : {},
      }}
    >
      <DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} aria-hidden="true" />
    </Box>
  );

  // Compact, touch-friendly sort control used in place of clickable table
  // headers when the table collapses into a card list on mobile.
  const MOBILE_SORT_OPTIONS = [
    { key: 'uploadTime', label: 'Date' },
    { key: 'status', label: 'Status' },
    { key: 'title', label: 'Title' },
  ];

  const renderMobileSortBar = () => (
    <Box sx={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', mb: '0.75rem', pb: '0.25rem', WebkitOverflowScrolling: 'touch' }}>
      {MOBILE_SORT_OPTIONS.map((opt) => {
        const isActive = sortConfig.key === opt.key;
        return (
          <Box
            key={opt.key}
            component="button"
            onClick={() => handleSort(opt.key)}
            aria-label={`Sort by ${opt.label}${isActive ? (sortConfig.direction === 'desc' ? ', descending' : ', ascending') : ''}`}
            sx={{
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.375rem 0.75rem', borderRadius: '1.25rem',
              border: `1px solid ${isActive ? COLORS.primary : COLORS.hairline}`,
              backgroundColor: isActive ? COLORS.primaryLight : '#fff',
              color: isActive ? COLORS.primaryDark : COLORS.textMuted,
              fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap',
              cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
            }}
          >
            {opt.label}
            {isActive ? (
              sortConfig.direction === 'desc'
                ? <ArrowDownwardIcon sx={{ fontSize: '0.875rem' }} />
                : <ArrowUpwardIcon sx={{ fontSize: '0.875rem' }} />
            ) : (
              <UnfoldMoreIcon sx={{ fontSize: '0.875rem' }} />
            )}
          </Box>
        );
      })}
    </Box>
  );

  const renderMobileCard = (session) => {
    const treeReady = isTreeReady(session.status, session.link, session.emptyTranscript);
    const isEmptyTranscript = session.status?.toLowerCase() === 'completed' && session.emptyTranscript === true;
    const canDelete = treeReady || isEmptyTranscript || session.status?.toLowerCase().startsWith('error');

    return (
      <Box
        key={session.sessionId}
        sx={{
          border: `1px solid ${COLORS.hairline}`,
          borderRadius: '0.75rem',
          padding: '0.875rem',
          marginBottom: '0.75rem',
          backgroundColor: session.isSimulated ? SIMULATED_ROW_BG : '#F9F7F6',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '0.625rem' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#111827', fontWeight: 500 }}>
              {toUploadDate(session.uploadTime).toLocaleDateString('en-US', {
                month: '2-digit', day: '2-digit', year: 'numeric'
              })}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, mt: '0.125rem' }}>
              {toUploadDate(session.uploadTime).toLocaleTimeString()}
            </Typography>
          </Box>
          {renderDeleteButton(session, canDelete)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: '0.625rem' }}>
          {renderStatusIndicator(session)}
        </Box>

        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', mb: session.description ? '0.25rem' : 0 }}>
          {session.title || 'Untitled Session'}
        </Typography>
        {session.description && (
          <Typography sx={{ fontSize: '0.75rem', color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {session.description}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box component="section" aria-label="Recordings" sx={{ padding: isMobile ? '0.625rem 0.5rem' : '0.625rem' }}>
      {toast && (
        <Box
          onClick={onToastClose}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
          }}
        />
      )}

      <AppSnackbar toast={toast} onClose={onToastClose} />
      <AppSnackbar toast={deleteToast} onClose={() => setDeleteToast(null)} />

      <Dialog
        open={dialogLabel !== null}
        onClose={() => setDialogLabel(null)}
        aria-labelledby="status-dialog-title"
        fullWidth
        PaperProps={{ sx: { borderRadius: '0.75rem', p: 1, maxWidth: '25rem' } }}
      >
        <DialogTitle id="status-dialog-title" sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {dialogLabel}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.text, lineHeight: 1.6 }}>
            {LABEL_DIALOG_CONTENT[dialogLabel] ?? 'Your recording is being analyzed. This can take up to 30 minutes depending on the length of the recording and current server load.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogLabel(null)}
            variant="contained"
            size="small"
            aria-label="Close status information dialog"
            sx={{
              backgroundColor: COLORS.primaryDark,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderRadius: '0.25rem',
              px: 2.5,
              boxShadow: 'none',
              '&:hover': BUTTON_HOVER_SX,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '1rem', padding: '0.5rem', maxWidth: '28.75rem', width: '100%' },
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 700, fontSize: '1.0625rem', color: '#111827', pb: 0.5 }}>
          {sessionToDelete?.isSimulated ? 'Delete Simulated Tree' : 'Delete Recording'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem', color: COLORS.text, lineHeight: 1.6 }}>
            Are you sure you want to delete {sessionToDelete?.isSimulated ? 'the simulated tree' : 'the data for the Talk Tree'}{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#111827' }}>
              "{sessionToDelete?.title}"
            </Box>
            ?
            <Box component="p" sx={{ mt: 1.5, mb: 0, color: '#dc2626', fontSize: '0.8125rem', fontWeight: 500 }}>
              This action cannot be undone.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            aria-label="Cancel delete"
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
              color: COLORS.text, borderRadius: '0.25rem', px: 2.5, border: '1px solid #d1d5db',
              '&:hover': { backgroundColor: '#f3f4f6' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            variant="contained"
            aria-label={`Confirm delete recording: ${sessionToDelete?.title}`}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
              backgroundColor: '#dc2626', borderRadius: '0.25rem', px: 2.5, boxShadow: 'none',
              '&:hover': { backgroundColor: '#b91c1c', boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: '#fca5a5', color: '#fff' },
            }}
          >
            {isDeleting ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageHelpDialog open={dataInfoOpen} onClose={() => setDataInfoOpen(false)} />

      {/* ── Data processing help link ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box
          onClick={() => setDataInfoOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDataInfoOpen(true); } }}
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
        >
          <Typography sx={{ fontSize: '14px', color: COLORS.primary, fontWeight: 500 }}>
            Help me understand how you handle and process my data
          </Typography>
          <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
            i
          </Box>
        </Box>
      </Box>

      {/* ── Header ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '0.75rem' : 0,
        mb: 2,
      }}>
        <Typography component="h1" sx={{ fontSize: '1.25rem', fontWeight: 400, color: COLORS.text, fontFamily: FONT_HEADING }}>
          Manage Trees
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '0.5rem' : 2,
        }}>
          {lastUpdated && (
            <Typography aria-live="polite" aria-atomic="true" sx={{ fontSize: '0.8125rem', color: COLORS.textMuted, textAlign: isMobile ? 'left' : 'inherit' }}>
              Last Updated: {formatLastUpdated(lastUpdated)}
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh recordings list"
            fullWidth={isMobile}
            sx={{
              backgroundColor: COLORS.primaryDark,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderRadius: '0.25rem',
              px: 2.5,
              py: 0.8,
              boxShadow: 'none',
              '&:hover': BUTTON_HOVER_SX,
              '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#fff' },
            }}
          >
            {isLoading ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} aria-hidden="true" /> : null}
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}

      {!userId ? (
        <Alert severity="info" role="status">No logged-in user found. Please log in to view recordings.</Alert>
      ) : isLoading ? (
        <Box role="status" aria-label="Loading recordings" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-hidden="true" />
        </Box>
      ) : allRows.length === 0 ? (
        <Box role="status" sx={{ textAlign: 'center', py: 10, border: `1px dashed ${COLORS.hairline}`, borderRadius: '1.25rem' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No recordings yet</Typography>
          <Typography variant="body2" color="text.secondary">Upload a recording to get started.</Typography>
        </Box>
      ) : isMobile ? (
        <Box>
          {renderMobileSortBar()}
          {sortedSessions.map(renderMobileCard)}
        </Box>
      ) : (
        <div style={{ border: `1px solid ${COLORS.hairline}`, borderRadius: '1.25rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table
            style={{ width: '100%', minWidth: '37.5rem', borderCollapse: 'collapse', tableLayout: 'auto' }}
            aria-label="Recordings list"
          >
            <thead>
              <tr>
                <SortableHeader label="Created Date"        sortKey="uploadTime"   sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Processing Status" sortKey="status"        sortConfig={sortConfig} onSort={handleSort} style={{ textAlign: 'center' }} />
                <SortableHeader label="Recording Title"   sortKey="title"         sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Description"       sortKey="description"   sortConfig={sortConfig} onSort={handleSort} style={{ width: '100%' }} />
                <th style={{ ...thBase, width: '3rem', textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              {sortedSessions.map((session) => {
                const treeReady = isTreeReady(session.status, session.link);
                const canDelete = session.isSimulated || treeReady || session.status?.toLowerCase().startsWith('error');
                const rowBg = session.isSimulated ? SIMULATED_ROW_BG : '#F9F7F6';
                return (
                  <tr
                    key={session.sessionId}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <td style={{ ...td, backgroundColor: rowBg }}>
                      <div style={{ color: '#111827', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {toUploadDate(session.uploadTime).toLocaleDateString('en-US', {
                          month: '2-digit', day: '2-digit', year: 'numeric'
                        })}
                      </div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', marginTop: '0.125rem', whiteSpace: 'nowrap' }}>
                        {toUploadDate(session.uploadTime).toLocaleTimeString()}
                      </div>
                    </td>

                    <td style={{ ...td, backgroundColor: rowBg, textAlign: 'center' }}>
                      {renderStatusIndicator(session)}
                    </td>

                    <td style={{ ...td, backgroundColor: rowBg, color: '#111827', whiteSpace: 'nowrap', textAlign: 'left', fontSize: '0.75rem' }}>
                      {session.title || 'Untitled Session'}
                    </td>

                    <td style={{ ...td, backgroundColor: rowBg, color: '#111827', fontSize: '0.75rem', textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                      {session.description || '—'}
                    </td>

                    {/* trash icon */}
                    <td style={{ ...td, backgroundColor: rowBg, textAlign: 'center', padding: '0.875rem 0.75rem' }}>
                      {renderDeleteButton(session, canDelete)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Box>
  );
};

export default SessionStatus;